import { useQuery } from '@tanstack/react-query';

import { listAiProviders } from '@/lib/api/generated/api';

import { aiProviderKeys } from '../queryKeys';
import type { AiProvider } from '../types';

/**
 * Every AI Provider defined on the organization.
 *
 * Mirrors `llm-profiles/hooks/useLlmProfiles.ts`: anything but a 200 throws
 * rather than resolving to an empty list, so the caller can tell "this
 * organization has none" from "we could not find out" and never render a
 * confident empty state over a failed request. The generated `listAiProviders`
 * resolves to `{ items: AIProviderView[] }`, not a bare array (the interim
 * hand-written contract's assumption) — unwrapped here.
 */
export function useAiProviders() {
  const query = useQuery({
    queryKey: aiProviderKeys.list(),
    queryFn: async (): Promise<AiProvider[]> => {
      const response = await listAiProviders();
      if (response.status !== 200) {
        throw new Error('The AI Providers could not be loaded.');
      }
      return response.data.items;
    },
    retry: false,
  });

  return {
    entries: query.data,
    isPending: query.isPending,
    loadFailed: query.isError,
    refetch: query.refetch,
  };
}
